import { HPC_COLORS } from "./hpcTheme";
import { HPC_BRAND } from "./hpcBrand";

// Draws the registration success card as a PNG with the browser Canvas API.
// The image is built from data (not a screenshot), so it always lists every member and never
// includes buttons.

const WIDTH = 720;
const SCALE = 2;
const OUTER = 32;
const PAD = 40;
const CARD_WIDTH = WIDTH - OUTER * 2;
const INNER = CARD_WIDTH - PAD * 2;
const FONT = '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const MONO = 'Consolas, "Courier New", monospace';
const CARD_FILL = "#11294a";

const font = (size, weight = 400, family = FONT) => `${weight} ${size}px ${family}`;

const loadImage = (src) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });

const wrapText = (ctx, text, maxWidth) => {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth) {
      line = candidate;
      return;
    }
    if (line) lines.push(line);
    // A single word longer than the line is broken by characters.
    let rest = word;
    while (ctx.measureText(rest).width > maxWidth && rest.length > 1) {
      let cut = rest.length - 1;
      while (cut > 1 && ctx.measureText(rest.slice(0, cut)).width > maxWidth) cut -= 1;
      lines.push(rest.slice(0, cut));
      rest = rest.slice(cut);
    }
    line = rest;
  });
  if (line) lines.push(line);
  return lines.length ? lines : [""];
};

const roundedRect = (ctx, x, y, width, height, radius) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + width, y, x + width, y + height, radius);
  ctx.arcTo(x + width, y + height, x, y + height, radius);
  ctx.arcTo(x, y + height, x, y, radius);
  ctx.arcTo(x, y, x + width, y, radius);
  ctx.closePath();
};

/**
 * Build the list of things to draw. Each item has a height and a draw(ctx, top) function,
 * so the canvas height can be known before drawing.
 */
const buildItems = (ctx, data, logo) => {
  const items = [];
  const add = (height, draw) => items.push({ height, draw });
  const centerX = OUTER + CARD_WIDTH / 2;
  const left = OUTER + PAD;

  const text = (value, size, { weight = 400, color = HPC_COLORS.white, family = FONT, gap = 8, align = "center" } = {}) => {
    ctx.font = font(size, weight, family);
    const lines = wrapText(ctx, value, INNER);
    const lineHeight = Math.round(size * 1.3);
    add(lines.length * lineHeight + gap, (c, top) => {
      c.font = font(size, weight, family);
      c.fillStyle = color;
      c.textAlign = align;
      c.textBaseline = "top";
      lines.forEach((line, index) => {
        c.fillText(line, align === "center" ? centerX : left, top + index * lineHeight);
      });
    });
  };

  const divider = (space = 20) => {
    add(space * 2, (c, top) => {
      c.strokeStyle = "rgba(255, 255, 255, 0.14)";
      c.lineWidth = 1;
      c.beginPath();
      c.moveTo(left, top + space);
      c.lineTo(left + INNER, top + space);
      c.stroke();
    });
  };

  if (logo) {
    const logoWidth = 150;
    const logoHeight = Math.round((logo.height / logo.width) * logoWidth) || 80;
    add(logoHeight + 10, (c, top) => c.drawImage(logo, centerX - logoWidth / 2, top, logoWidth, logoHeight));
  }
  text(HPC_BRAND.name, 15, { color: HPC_COLORS.silver, gap: 18 });
  text("Registration confirmed", 30, { weight: 700, gap: 10 });
  text(
    [data.statusText ? `Status: ${data.statusText}` : "", data.dateText ? `Registered on ${data.dateText}` : ""]
      .filter(Boolean)
      .join("  ·  "),
    17,
    { color: HPC_COLORS.silver, gap: 4 },
  );

  const isGroup = Boolean(data.groupId);
  divider();

  if (isGroup) {
    text(data.isFamily ? "Family group ID" : "Group ID", 17, { color: HPC_COLORS.tebaBlue, weight: 600, gap: 4 });
    text(data.groupId, 36, { weight: 800, color: HPC_COLORS.hpuOrange, family: MONO, gap: 6 });
    text(`${data.members.length} members`, 15, { color: HPC_COLORS.silver, gap: 4 });
    divider(16);

    // Member rows: name (wraps) and role on the left, membership ID on the right.
    const idWidth = 230;
    const nameWidth = INNER - idWidth - 16;
    data.members.forEach((member, index) => {
      ctx.font = font(21, 700);
      const nameLines = wrapText(ctx, member.full_name || "Member", nameWidth);
      const nameLineHeight = 27;
      const rowHeight = nameLines.length * nameLineHeight + 26 + (index < data.members.length - 1 ? 22 : 0);
      add(rowHeight, (c, top) => {
        c.textBaseline = "top";
        c.textAlign = "left";
        c.font = font(21, 700);
        c.fillStyle = HPC_COLORS.white;
        nameLines.forEach((line, lineIndex) => c.fillText(line, left, top + lineIndex * nameLineHeight));
        c.font = font(15);
        c.fillStyle = HPC_COLORS.silver;
        c.fillText(member.roleText, left, top + nameLines.length * nameLineHeight + 2);
        c.textAlign = "right";
        c.font = font(21, 800, MONO);
        c.fillStyle = HPC_COLORS.hpuOrange;
        c.fillText(member.membership_id || "", left + INNER, top + 2);
        if (index < data.members.length - 1) {
          const lineY = top + rowHeight - 11;
          c.strokeStyle = "rgba(255, 255, 255, 0.08)";
          c.beginPath();
          c.moveTo(left, lineY);
          c.lineTo(left + INNER, lineY);
          c.stroke();
        }
      });
    });
  } else {
    if (data.fullName) text(data.fullName, 22, { weight: 700, gap: 10 });
    text("Membership ID", 17, { color: HPC_COLORS.tebaBlue, weight: 600, gap: 4 });
    text(data.membershipId, 40, { weight: 800, color: HPC_COLORS.hpuOrange, family: MONO, gap: 4 });
  }

  divider();
  text(
    isGroup ? "Save these IDs for check-in and future visits." : "Save this ID for check-in and future visits.",
    16,
    { color: HPC_COLORS.silver, gap: 0 },
  );
  return items;
};

/** Render the success card data to a PNG Blob. */
export const createSuccessImage = async (data) => {
  const logo = await loadImage(HPC_BRAND.logoSrc);
  const measure = document.createElement("canvas").getContext("2d");
  const items = buildItems(measure, data, logo);
  const contentHeight = items.reduce((sum, item) => sum + item.height, 0);
  const cardHeight = contentHeight + PAD * 2;
  const height = cardHeight + OUTER * 2;

  const canvas = document.createElement("canvas");
  canvas.width = WIDTH * SCALE;
  canvas.height = height * SCALE;
  const ctx = canvas.getContext("2d");
  ctx.scale(SCALE, SCALE);

  const background = ctx.createLinearGradient(0, 0, 0, height);
  background.addColorStop(0, HPC_COLORS.navy);
  background.addColorStop(1, HPC_COLORS.navyMid);
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, WIDTH, height);
  const glow = ctx.createRadialGradient(WIDTH / 2, 0, 0, WIDTH / 2, 0, WIDTH * 0.6);
  glow.addColorStop(0, "rgba(245, 166, 35, 0.22)");
  glow.addColorStop(1, "rgba(245, 166, 35, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, WIDTH, height);

  roundedRect(ctx, OUTER, OUTER, CARD_WIDTH, cardHeight, 16);
  ctx.fillStyle = CARD_FILL;
  ctx.fill();
  ctx.strokeStyle = "rgba(245, 166, 35, 0.5)";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  let top = OUTER + PAD;
  items.forEach((item) => {
    item.draw(ctx, top);
    top += item.height;
  });

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not create the image."))), "image/png");
  });
};

export const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
